import { provideRouter, Routes, withPreloading } from '@angular/router';
import { AuthGuard } from './shared/guards/auth.guard';
import { AdminGuard } from './shared/guards/admin.guard';
import { NavigationBlockManualGuard } from './shared/guards/navigation-block-manual.guard';
import { REUSE_ROUTE_DATA_KEY } from './custom-route-reuse.strategy';
import {
  PRELOAD_ROUTE_DATA_KEY,
  SelectivePreloadingStrategy,
} from './shared/routing/selective-preloading.strategy';
import { PageMetaResolver } from './seo/page-meta.resolver';
import { cityFeatureMatcher, featureOnlyMatcher } from './seo/route-matchers';
import {
  INDUSTRY_SLUG_KEY,
  SALES_FORECAST_BASE_PATH,
  SALES_FORECAST_INDEX_TITLE,
  SALES_FORECAST_ROUTES,
  SALES_FORECAST_THANK_YOU_PATH,
  salesForecastPath,
} from './sales-forecast/sales-forecast.routes-data';
import { PRICING_PAGE_TITLE } from './shared/util/consts';
import { preloadMapbox } from './shared/mapbox/mapbox-loader';
import { lazyChunk } from './shared/routing/lazy-chunk';
import { withChunkRecovery } from './shared/routing/chunk-recovery';

/**
 * Wraps a lazy `loadComponent`/`loadChildren` so the Mapbox CDN bundles start
 * downloading at the same instant as the route chunk, rather than after it.
 *
 * Mapbox used to be two `defer` <script>s in index.html, which cost 245 KB on
 * every page view and, worse, put ~800 KB of parse in front of Angular's
 * bootstrap on every route (see todos/optimization.md item 4). Moving it
 * on-demand would otherwise trade that for a serial waterfall on the map
 * routes: chunk, then script, then map. This keeps the two in parallel.
 *
 * Deliberately colocated with the lazy import rather than kept as a list of
 * route prefixes somewhere else - a new map route cannot forget to opt in,
 * because the opt-in is on the same line as the import it belongs to.
 */
const withMapbox = <T>(
  load: () => Promise<T>,
  options?: { geocoder?: boolean }
) => {
  const loader = () => {
    preloadMapbox(options);
    return load();
  };
  // Read by assertMapboxRoutesAreNotPreloaded below
  (loader as { __mapbox?: boolean }).__mapbox = true;
  return loader;
};

/**
 * `withMapbox` MUST NOT be combined with `PRELOAD_ROUTE_DATA_KEY`.
 *
 * `SelectivePreloadingStrategy` preloads a flagged route by calling the very
 * `loadComponent`/`loadChildren` function `withMapbox` wraps - so a route that
 * is both wrapped and flagged would fetch 245 KB of Mapbox from the CDN on
 * *every* page in the app, once idle, silently undoing item 4. There is no
 * argument the wrapper can inspect to tell a navigation from a preload, so the
 * invariant is checked here instead of left as a comment for someone to miss.
 */
function assertMapboxRoutesAreNotPreloaded(routes: Routes): void {
  for (const route of routes) {
    const loader = (route.loadComponent ?? route.loadChildren) as
      | { __mapbox?: boolean }
      | undefined;
    if (loader?.__mapbox && route.data?.[PRELOAD_ROUTE_DATA_KEY] === true) {
      console.error(
        `[mapbox] route "${route.path ?? '(matcher)'}" combines withMapbox() with ` +
          `${PRELOAD_ROUTE_DATA_KEY}: true, which fetches Mapbox on every page. ` +
          'Drop one of them - preload Mapbox from the feature component instead.'
      );
    }
    if (route.children) assertMapboxRoutesAreNotPreloaded(route.children);
  }
}

export const appRoutes: Routes = [
  // SEO matching for city/feature routes
  {
    matcher: cityFeatureMatcher,
    loadComponent: withMapbox(
      lazyChunk(
        () => import('./map/map.component'),
        (c) => c.MapComponent
      ),
      { geocoder: true }
    ),
    canActivate: [NavigationBlockManualGuard],
    resolve: { meta: PageMetaResolver },
  },
  {
    matcher: featureOnlyMatcher,
    loadComponent: withMapbox(
      lazyChunk(
        () => import('./map/map.component'),
        (c) => c.MapComponent
      ),
      { geocoder: true }
    ),
    canActivate: [NavigationBlockManualGuard],
    resolve: { meta: PageMetaResolver },
  },

  {
    path: 'info',
    title: 'Info - Map AI',
    loadChildren: lazyChunk(
      () => import('./support/info.module'),
      (m) => m.InfoModule
    ),
    canActivate: [NavigationBlockManualGuard],
  },
  {
    path: 'admin',
    title: 'Admin - Map AI',
    loadComponent: lazyChunk(
      () => import('./admin/admin.component'),
      (c) => c.AdminComponent
    ),
    canMatch: [AdminGuard],
    canActivate: [AdminGuard],
  },
  {
    path: 'comparative-location-tool',
    title: 'Comparative Location Tool - Map AI',
    loadComponent: withMapbox(
      lazyChunk(
        () =>
          import(
            './comparative-location-tool/comparative-location-tool.component'
          ),
        (c) => c.ComparativeLocationToolComponent
      ),
      { geocoder: true }
    ),
    // No AuthGuard: the tool is open to anonymous visitors. What is paid for
    // is gated inside the table itself (per-feature access) and by the
    // sign-in prompt on the AI summary.
    canActivate: [NavigationBlockManualGuard],
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'reports',
    // NOT withMapbox(): this route is preloaded, and the preloader calls the
    // same function. OrderReportComponent starts the fetch instead.
    loadChildren: lazyChunk(
      () => import('./reports/reports.routes'),
      (r) => r.reportsRoutes
    ),
    canActivate: [NavigationBlockManualGuard],
    data: { [REUSE_ROUTE_DATA_KEY]: true, [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'user',
    title: 'User - Map AI',
    loadChildren: lazyChunk(
      () => import('./user/user.routes'),
      (r) => r.userRoutes
    ),
    canActivate: [NavigationBlockManualGuard, AuthGuard],
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-in',
    loadComponent: lazyChunk(
      () => import('./user/sign-in/sign-in.component'),
      (c) => c.SignInComponent
    ),
    canActivate: [NavigationBlockManualGuard],
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'password-recovery',
    loadComponent: lazyChunk(
      () =>
        import('./user/forgot-password-page/forgot-password-page.component'),
      (c) => c.ForgotPasswordPageComponent
    ),
  },
  {
    path: 'user/password/reset',
    loadComponent: lazyChunk(
      () =>
        import(
          './user/forgot-password-page/reset-password/reset-password.component'
        ),
      (c) => c.ResetPasswordComponent
    ),
  },
  {
    path: 'sign-up',
    loadComponent: lazyChunk(
      () => import('./user/sign-up/sign-up.component'),
      (c) => c.SignUpComponent
    ),
    canActivate: [NavigationBlockManualGuard],
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-up/individual',
    loadComponent: lazyChunk(
      () =>
        import(
          './user/sign-up/register-individual/register-individual.component'
        ),
      (c) => c.RegisterIndividualComponent
    ),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-up/enterprise',
    loadComponent: lazyChunk(
      () =>
        import(
          './user/sign-up/register-individual/register-individual.component'
        ),
      (c) => c.RegisterIndividualComponent
    ),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'sign-up/success',
    loadComponent: lazyChunk(
      () =>
        import(
          './user/sign-up/register-individual/registration-success-window/registration-success-window.component'
        ),
      (c) => c.RegistrationSuccessWindowComponent
    ),
  },
  // Sales Forecast industry hub — lists and links every industry page
  {
    path: SALES_FORECAST_BASE_PATH,
    title: SALES_FORECAST_INDEX_TITLE,
    loadComponent: lazyChunk(
      () =>
        import(
          './sales-forecast/index-page/sales-forecast-index-page.component'
        ),
      (c) => c.SalesForecastIndexPageComponent
    ),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  // Sales Forecast landing pages: one static route per industry so each can
  // target its own keywords (deliberately no :industry param / dropdown).
  // Slugs/titles come from sales-forecast.routes-data.ts — the same source
  // the sitemap generator uses.
  ...SALES_FORECAST_ROUTES.map((industryRoute) => ({
    path: salesForecastPath(industryRoute.slug),
    title: industryRoute.title,
    loadComponent: lazyChunk(
      () =>
        import(
          './sales-forecast/sales-forecast-page/sales-forecast-page.component'
        ),
      (c) => c.SalesForecastPageComponent
    ),
    data: { [INDUSTRY_SLUG_KEY]: industryRoute.slug },
  })),
  // Stripe success_url target for the sales-forecast checkout — noindex,
  // not linked from nav/sitemap
  {
    path: SALES_FORECAST_THANK_YOU_PATH,
    loadComponent: lazyChunk(
      () =>
        import(
          './sales-forecast/thank-you-page/sales-forecast-thank-you-page.component'
        ),
      (c) => c.SalesForecastThankYouPageComponent
    ),
  },
  {
    path: 'pricing',
    // Same constant pricingPageTags uses - see PRICING_PAGE_TITLE.
    title: PRICING_PAGE_TITLE,
    loadComponent: lazyChunk(
      () => import('./pricing/pricing-page/pricing-page.component'),
      (c) => c.PricingPageComponent
    ),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'chat',
    // NOT withMapbox(): this route is preloaded, and the preloader calls the
    // same function. Nothing replaces it and nothing needs to - AiMapPageComponent,
    // ChatAndMapComponent and the map itself all ship in this one chunk, so the
    // map's own ngOnInit asks for Mapbox in the same change-detection pass.
    loadComponent: lazyChunk(
      () => import('./ai-map/ai-map-page/ai-map-page.component'),
      (c) => c.AiMapPageComponent
    ),
    data: { [PRELOAD_ROUTE_DATA_KEY]: true },
  },
  {
    path: 'chat/about',
    loadComponent: lazyChunk(
      () => import('./ai-map/about-ai-map/about-ai-map.component'),
      (c) => c.AboutAiMapComponent
    ),
  },
  {
    path: '',
    title: 'Map AI',
    loadComponent: withMapbox(
      lazyChunk(
        () => import('./map/map.component'),
        (c) => c.MapComponent
      ),
      { geocoder: true }
    ),
    canActivate: [NavigationBlockManualGuard],
    data: { [REUSE_ROUTE_DATA_KEY]: true },
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];

assertMapboxRoutesAreNotPreloaded(appRoutes);

export const appRoutingProviders = provideRouter(
  appRoutes,
  withPreloading(SelectivePreloadingStrategy),
  // Finishes a navigation `lazyChunk` could not retry its way out of - see
  // chunk-recovery.ts.
  withChunkRecovery()
);
